<template>
  <div
    class="custom-select"
    :class="`custom-select--${mode} custom-select--align-${alignText}`"
    v-click-outside="hideOptions"
  >
    <div class="custom-select__title">
      {{ translatedTitle }}
    </div>
    <div class="custom-select__header" @click.stop="toggleList">
      <div class="custom-select__selected" :class="{ active: value !== null }">
        <img
          v-if="showIcons && selectedOption"
          :src="getIconUrl(selectedOption)"
          @error="onIconError($event)"
          class="custom-select__option-icon"
          alt=""
        />
        {{
          selectedOption
            ? translate
              ? $t(selectedOption.name)
              : selectedOption.name
            : translatedPlaceholder
        }}
      </div>
      <div class="custom-select__caret" :class="{ toggle: isShowList }">
        <svg
          width="8"
          height="5"
          viewBox="0 0 8 5"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M4.26688 4.52433L7.78955 1.0016C7.87109 0.920118 7.91602 0.811354 7.91602 0.695382C7.91602 0.57941 7.87109 0.470646 7.78955 0.38917L7.53019 0.129745C7.36119 -0.0390649 7.08651 -0.0390649 6.91777 0.129745L3.95964 3.08787L0.998234 0.126462C0.916693 0.0449859 0.807993 0 0.692085 0C0.576049 0 0.467349 0.0449859 0.385744 0.126462L0.126448 0.385887C0.0449071 0.467428 -1.43051e-05 0.576128 -1.43051e-05 0.6921C-1.43051e-05 0.808072 0.0449071 0.916836 0.126448 0.998313L3.65233 4.52433C3.73413 4.606 3.84335 4.65086 3.95945 4.6506C4.076 4.65086 4.18515 4.606 4.26688 4.52433Z"
            fill="#666D79"
          />
        </svg>
      </div>
    </div>
    <div class="custom-select__list" v-if="isShowList">
      <div class="custom-select__list-search" v-if="canSearch">
        <text-input
          mode="small"
          :placeholder="$t('custom-select.search')"
          @update:value="onSearch()"
          v-model:value="searchQuery"
        ></text-input>
      </div>
      <div ref="list" class="custom-select__list-scroll">
        <div
          class="custom-select__list-item"
          @click="selectOption(null)"
          v-if="hasDefaultValue"
        >
          {{ $t('custom-select.not-selected') }}
        </div>
        <div
          class="custom-select__list-item"
          v-for="option in optionsFiltered"
          @click="selectOption(option)"
          :class="{
            active:
              selectedOption &&
              selectedOption[valueKey ? valueKey : 'id'] ===
                option[valueKey ? valueKey : 'id'],
          }"
          :key="`option-${option[valueKey ? valueKey : 'id']}`"
        >
          <img
            v-if="showIcons"
            :src="getIconUrl(option)"
            @error="onIconError($event)"
            class="custom-select__option-icon"
            alt=""
          />
          {{ translate ? $t(option.name) : option.name }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import TextInput from '@vue-app/components/base/input/TextInput.vue'

export default {
  name: 'CustomSelect',
  components: { TextInput },
  props: {
    showIcons: {
      type: Boolean,
      default: false,
    },
    iconPathPrefix: {
      type: String,
      default: '',
    },
    title: {
      type: [String, Object],
      default: '',
    },
    hint: {
      type: [String, Object],
      default: null,
    },
    value: {
      type: [Object, String, Number],
    },
    translate: {
      type: Boolean,
      default: true,
    },
    options: {
      type: [Array, Object],
      default() {
        return []
      },
    },
    placeholder: {
      type: [String, Object],
      default: 'custom-select.choose-option',
    },
    mode: {
      type: String,
      default: 'base',
    },
    valueKey: {
      type: String,
      default: null,
    },
    hasDefaultValue: {
      type: Boolean,
      default: true,
    },
    shouldTranslateTitle: {
      type: Boolean,
      default: false,
    },
    localizedTitle: {
      type: Boolean,
      default: false,
    },
    alignText: {
      type: String,
      default: 'center',
    },
    canSearch: {
      type: Boolean,
      default: false,
    },
    startNavigateOnSearch: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isShowList: false,
      searchQuery: '',
    }
  },
  computed: {
    locale() {
      let lang = this.$i18n?.locale ?? 'en'
      return lang === 'ua' ? 'uk' : lang
    },
    optionsFiltered() {
      if (!this.canSearch) {
        return this.options
      }

      if (this.searchQuery.trim().length === 0) {
        return this.options
      }

      return this.options.filter(
        (_option) =>
          _option.name.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
          _option.code?.toLowerCase().includes(this.searchQuery.toLowerCase())
      )
    },

    selectedOption() {
      return this.valueKey
        ? this.options.find((_option) => _option[this.valueKey] === this.value)
        : this.value
    },
    translatedTitle() {
      if (this.shouldTranslateTitle) {
        return this.$t(this.title)
      }

      if (this.localizedTitle && typeof this.title === 'object') {
        return this.title[this.locale] ?? this.title['en'] ?? ''
      }

      return this.title
    },
    translatedPlaceholder() {
      if (this.shouldTranslateTitle) {
        return this.$t(this.placeholder)
      }

      if (this.localizedTitle && typeof this.placeholder === 'object') {
        return this.placeholder[this.locale] ?? this.placeholder['en'] ?? ''
      }

      return this.$t(this.placeholder) ?? this.placeholder
    },
    translatedHint() {
      if (this.shouldTranslateTitle) {
        return this.$t(this.hint)
      }

      if (this.localizedTitle && typeof this.hint === 'object') {
        return this.hint[this.locale] ?? this.hint['en'] ?? ''
      }

      return this.hint
    },
  },
  methods: {
    isSelected(option) {
      if (this.valueKey) {
        return this.value === option[this.valueKey]
      }
      return this.value && this.value.id === option.id
    },
    getIconUrl(option) {
      if (!this.showIcons || !this.iconPathPrefix) return null
      const rawKey = this.valueKey
        ? option[this.valueKey]
        : (option.value ?? option.name ?? '').toString()
      return `${this.iconPathPrefix}/${rawKey}.svg`
    },
    onIconError(event) {
      if (this.iconPathPrefix) {
        event.target.src = `${this.iconPathPrefix}/default.svg`
      }
    },
    onSearch() {
      if (this.startNavigateOnSearch) {
        setTimeout(() => {
          const listOptions = this.$refs.list.querySelectorAll(
            '.custom-select__list-item'
          )
          listOptions?.[0].scrollIntoView()
        }, 0)
      }
    },
    selectOption(option) {
			const next = !option
				? null
				: (this.valueKey ? option[this.valueKey] : option)

			this.$emit('update:value', next)
			this.$emit('change', next)
			this.hideOptions()
    },

    hideOptions() {
      this.isShowList = false
    },
    toggleList() {
      this.isShowList = !this.isShowList
    },
  },
  emits: ['change', 'update:value'],
}
</script>

<style lang="scss" scoped>
.custom-select {
  position: relative;
  &__title {
    font-weight: 300;
    font-size: 11px;
    line-height: 13px;
    color: #909cb0;
    padding-left: 10px;
    margin-bottom: 5px;
  }

  &__header {
    padding: 13px 58px 13px 21px;
    height: auto;
    border: 1px solid #f2f4f7;
    border-radius: 8px;
    box-shadow: unset;

    font-weight: 400;
    font-size: 12px;
    line-height: 15px;
    color: #666d79;

    position: relative;
    cursor: default;
  }

  &--big & {
    &__title {
      font-weight: 400;
      font-size: 12px;
      line-height: 15px;
      color: #465a8b;
      padding: 2px 10px;
      position: absolute;
      top: 0;
      left: 15px;

      transform: translateY(-50%);
      background-color: #fff;
      z-index: 2;
    }

    &__header {
      width: 100%;
      border: 1px solid #e0e6f2;
      background-color: transparent;
      border-radius: 6px;
      padding: 24px 15px 24px 21px;
      font-weight: 500;
      font-size: 14px;
      line-height: 20px;
      color: #585f6d;
    }
  }

  &--align-left & {
    &__selected {
      text-align: left;
    }
  }

  &__selected {
    width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__caret {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);

    display: flex;
    align-items: center;
    justify-content: center;

    &.toggle {
      transform: translateY(-50%) rotate(-180deg);
    }
  }

  &__list {
    position: absolute;
    top: calc(100% + 10px);
    background: #ffffff;
    box-shadow: 0px -5px 60px rgba(120, 140, 184, 0.1);
    border-radius: 8px;
    padding: 17px 0;
    z-index: 99;
    min-width: 100%;
    max-height: 280px;
    overflow-y: hidden;

    display: flex;
    flex-direction: column;

    &-search {
      padding: 0 15px;
      margin-bottom: 10px;
      flex: 0 0 auto;
    }

    &-scroll {
      overflow-y: auto;
      flex: 1 1 auto;
    }

    &-item {
      padding: 10px 28px;
      font-weight: 500;
      font-size: 12px;
      line-height: 16px;
      color: #666d79;
      white-space: nowrap;
      cursor: pointer;

      &.active {
        background: rgba(#e0e6f2, 0.5);
        color: #64748B;
      }

      &:hover:not(.active) {
        background: rgba(#e0e6f2, 0.3);
        color: #64748B;
      }
    }
  }

  &__caret {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    display: flex;
    align-items: center;
    justify-content: center;

    &.toggle {
      transform: translateY(-50%) rotate(-180deg);
    }
  }

  &__selected {
    width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}
.custom-select__option-icon {
  width: 28px;
  height: 28px;
  margin-right: 8px;
  vertical-align: middle;
}
</style>
